/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-object-type */
import {
  Appointment,
  Company,
  Patient,
  Exam,
  JobRole,
  SiteInspection,
  Document,
  User,
  DailyNote,
  WeeklyNote,
  DoctorWeeklyAvailability,
  AvailabilityOverride,
  DoctorSlotDurationRule,
  Clinic,
  ClinicAssignment,
} from "../schemas";

export const EVENTS = {
  APPOINTMENT: {
    CREATED: "appointment:created",
    UPDATED: "appointment:updated",
    DELETED: "appointment:deleted",
  },
  PATIENT_CALLED: "patient:called",
  DAILY_NOTE: {
    UPDATED: "dailyNote:updated",
  },
  SCHEDULE_SLOT: {
    UPDATED: "scheduleSlot:updated",
  },
  SCHEDULE_NOTE: {
    UPDATED: "scheduleNote:updated",
  },
  AVAILABILITY: {
    DOCTOR_CHANGED: "doctor:availabilityChanged",
    WEEKLY_CHANGED: "availability:weeklyChanged",
    OVERRIDE_CREATED: "availability:overrideCreated",
    OVERRIDE_DELETED: "availability:overrideDeleted",
    SLOT_STATE_CHANGED: "slot:stateChanged",
    SLOT_DURATION_RULE_CHANGED: "slotDurationRule:changed",
  },
  COMPANY: {
    CREATED: "company:created",
    UPDATED: "company:updated",
    DELETED: "company:deleted",
  },
  PATIENT: {
    CREATED: "patient:created",
    UPDATED: "patient:updated",
    DELETED: "patient:deleted",
  },
  EXAM: {
    CREATED: "exam:created",
    UPDATED: "exam:updated",
    DELETED: "exam:deleted",
  },
  JOB_ROLE: {
    CREATED: "jobRole:created",
    UPDATED: "jobRole:updated",
    DELETED: "jobRole:deleted",
  },
  SITE_INSPECTION: {
    CREATED: "siteInspection:created",
    UPDATED: "siteInspection:updated",
    DELETED: "siteInspection:deleted",
  },
  DOCUMENT: {
    CREATED: "document:created",
    UPDATED: "document:updated",
    DELETED: "document:deleted",
  },
  USER: {
    CREATED: "user:created",
    UPDATED: "user:updated",
    DELETED: "user:deleted",
  },
  CLINIC: {
    CREATED: "clinic:created",
    UPDATED: "clinic:updated",
    DELETED: "clinic:deleted",
  },
  CLINIC_ASSIGNMENT: {
    CREATED: "clinicAssignment:created",
    UPDATED: "clinicAssignment:updated",
    DELETED: "clinicAssignment:deleted",
  },
  TICKET: {
    UPDATED: "ticket:updated",
  },
} as const;

export type EventType = typeof EVENTS;

// --- Payload Interfaces ---

export interface AppointmentCreatedPayload extends Appointment {}
export interface AppointmentUpdatedPayload extends Appointment {}
export interface AppointmentDeletedPayload {
  appointmentId: string;
  date: string;
  doctorId: string;
}

export interface PatientCalledPayload {
  appointmentId: string;
  patientName: string;
  doctorName: string;
  companyId: string;
  companyName: string;
  date: string;
  time: string;
  calledAt: string;
  calledBy: string;
}

export interface DailyNoteUpdatedPayload extends DailyNote {}

// Using a broader type for ScheduleSlot as it might contain extra UI props
export interface ScheduleSlotUpdatedPayload {
  id: string;
  weekId: string;
  [key: string]: any;
}

export interface ScheduleNoteUpdatedPayload extends WeeklyNote {}

export interface DoctorAvailabilityChangedPayload extends User {}

export interface WeeklyAvailabilityChangedPayload {
  doctorId: string;
  weeklyAvailability: DoctorWeeklyAvailability;
}

export interface OverrideCreatedPayload {
  doctorId: string;
  override: AvailabilityOverride;
}

export interface OverrideDeletedPayload {
  doctorId: string;
  overrideId: string;
  date: string;
}

export interface SlotStateChangedPayload {
  doctorId: string;
  date: string;
  time: string;
  state: string;
  appointmentId?: string;
}

export interface SlotDurationRuleChangedPayload {
  doctorId: string;
  rule: DoctorSlotDurationRule | null;
  affectedDates: string[];
}

export interface CompanyCreatedPayload extends Company {}
export interface CompanyUpdatedPayload extends Company {}
export interface CompanyDeletedPayload {
  id: string;
}

export interface PatientCreatedPayload extends Patient {}
export interface PatientUpdatedPayload extends Patient {}
export interface PatientDeletedPayload {
  id: string;
  companyId?: string;
}

export interface ExamCreatedPayload extends Exam {}
export interface ExamUpdatedPayload extends Exam {}
export interface ExamDeletedPayload {
  id: string;
}

export interface JobRoleCreatedPayload extends JobRole {}
export interface JobRoleUpdatedPayload extends JobRole {}
export interface JobRoleDeletedPayload {
  id: string;
}

export interface SiteInspectionCreatedPayload extends SiteInspection {}
export interface SiteInspectionUpdatedPayload extends SiteInspection {}
export interface SiteInspectionDeletedPayload {
  id: string;
}

export interface DocumentCreatedPayload extends Document {}
export interface DocumentUpdatedPayload extends Document {}
export interface DocumentDeletedPayload {
  id: string;
}

export interface UserCreatedPayload extends User {}
export interface UserUpdatedPayload extends User {}
export interface UserDeletedPayload {
  id: string;
}

export interface ClinicCreatedPayload extends Clinic {}
export interface ClinicUpdatedPayload extends Clinic {}
export interface ClinicDeletedPayload {
  id: string;
}

export interface ClinicAssignmentCreatedPayload extends ClinicAssignment {}
export interface ClinicAssignmentUpdatedPayload extends ClinicAssignment {}
export interface ClinicAssignmentDeletedPayload {
  id: string;
}

export interface TicketUpdatedPayload {
  id: string;
}

export interface WebSocketEvent<T = any> {
  event: string;
  data: T;
}

export interface ServerToClientEvents {
  [EVENTS.APPOINTMENT.CREATED]: (payload: AppointmentCreatedPayload) => void;
  [EVENTS.APPOINTMENT.UPDATED]: (
    payload: AppointmentUpdatedPayload,
    oldDate?: string,
  ) => void;
  [EVENTS.APPOINTMENT.DELETED]: (payload: AppointmentDeletedPayload) => void;

  [EVENTS.PATIENT_CALLED]: (payload: PatientCalledPayload) => void;
  [EVENTS.DAILY_NOTE.UPDATED]: (payload: DailyNoteUpdatedPayload) => void;
  [EVENTS.SCHEDULE_SLOT.UPDATED]: (payload: ScheduleSlotUpdatedPayload) => void;
  [EVENTS.SCHEDULE_NOTE.UPDATED]: (payload: ScheduleNoteUpdatedPayload) => void;

  [EVENTS.AVAILABILITY.DOCTOR_CHANGED]: (
    payload: DoctorAvailabilityChangedPayload,
  ) => void;
  [EVENTS.AVAILABILITY.WEEKLY_CHANGED]: (
    payload: WeeklyAvailabilityChangedPayload,
  ) => void;
  [EVENTS.AVAILABILITY.OVERRIDE_CREATED]: (
    payload: OverrideCreatedPayload,
  ) => void;
  [EVENTS.AVAILABILITY.OVERRIDE_DELETED]: (
    payload: OverrideDeletedPayload,
  ) => void;
  [EVENTS.AVAILABILITY.SLOT_STATE_CHANGED]: (
    payload: SlotStateChangedPayload,
  ) => void;
  [EVENTS.AVAILABILITY.SLOT_DURATION_RULE_CHANGED]: (
    payload: SlotDurationRuleChangedPayload,
  ) => void;

  [EVENTS.COMPANY.CREATED]: (payload: CompanyCreatedPayload) => void;
  [EVENTS.COMPANY.UPDATED]: (payload: CompanyUpdatedPayload) => void;
  [EVENTS.COMPANY.DELETED]: (payload: CompanyDeletedPayload) => void;

  [EVENTS.PATIENT.CREATED]: (payload: PatientCreatedPayload) => void;
  [EVENTS.PATIENT.UPDATED]: (payload: PatientUpdatedPayload) => void;
  [EVENTS.PATIENT.DELETED]: (payload: PatientDeletedPayload) => void;

  [EVENTS.EXAM.CREATED]: (payload: ExamCreatedPayload) => void;
  [EVENTS.EXAM.UPDATED]: (payload: ExamUpdatedPayload) => void;
  [EVENTS.EXAM.DELETED]: (payload: ExamDeletedPayload) => void;

  [EVENTS.JOB_ROLE.CREATED]: (payload: JobRoleCreatedPayload) => void;
  [EVENTS.JOB_ROLE.UPDATED]: (payload: JobRoleUpdatedPayload) => void;
  [EVENTS.JOB_ROLE.DELETED]: (payload: JobRoleDeletedPayload) => void;

  [EVENTS.SITE_INSPECTION.CREATED]: (
    payload: SiteInspectionCreatedPayload,
  ) => void;
  [EVENTS.SITE_INSPECTION.UPDATED]: (
    payload: SiteInspectionUpdatedPayload,
  ) => void;
  [EVENTS.SITE_INSPECTION.DELETED]: (
    payload: SiteInspectionDeletedPayload,
  ) => void;

  [EVENTS.DOCUMENT.CREATED]: (payload: DocumentCreatedPayload) => void;
  [EVENTS.DOCUMENT.UPDATED]: (payload: DocumentUpdatedPayload) => void;
  [EVENTS.DOCUMENT.DELETED]: (payload: DocumentDeletedPayload) => void;

  [EVENTS.USER.CREATED]: (payload: UserCreatedPayload) => void;
  [EVENTS.USER.UPDATED]: (payload: UserUpdatedPayload) => void;
  [EVENTS.USER.DELETED]: (payload: UserDeletedPayload) => void;

  [EVENTS.CLINIC.CREATED]: (payload: ClinicCreatedPayload) => void;
  [EVENTS.CLINIC.UPDATED]: (payload: ClinicUpdatedPayload) => void;
  [EVENTS.CLINIC.DELETED]: (payload: ClinicDeletedPayload) => void;

  [EVENTS.CLINIC_ASSIGNMENT.CREATED]: (
    payload: ClinicAssignmentCreatedPayload,
  ) => void;
  [EVENTS.CLINIC_ASSIGNMENT.UPDATED]: (
    payload: ClinicAssignmentUpdatedPayload,
  ) => void;
  [EVENTS.CLINIC_ASSIGNMENT.DELETED]: (
    payload: ClinicAssignmentDeletedPayload,
  ) => void;

  [EVENTS.TICKET.UPDATED]: (payload: TicketUpdatedPayload) => void;
}

export interface ClientToServerEvents {
  join: (room: string) => void;
  leave: (room: string) => void;
}
